// Opens a print-ready portrait page of one growth matrix with student names in
// the cells. Browser print -> Save as PDF covers the PDF ask without a server
// renderer. Everything is inlined so the page has no dependency on app CSS.

const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const LEVEL_ORDER = ['doesNotMeet', 'doesNotMeetHigh', 'approaches', 'approachesHigh', 'meets', 'masters'];
const LEVEL_RANK = LEVEL_ORDER.reduce((o, level, i) => {
  o[level] = i;
  return o;
}, {});

const movementClass = (prior, current) => {
  if (LEVEL_RANK[current] > LEVEL_RANK[prior]) return 'up';
  if (LEVEL_RANK[current] < LEVEL_RANK[prior]) return 'down';
  return 'same';
};

const namesList = (names) =>
  names && names.length ? `<ul>${names.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>` : '';

/**
 * @param {object} p
 * @param {string} p.title            e.g. "Reading — Growth Matrix"
 * @param {string} p.campusName
 * @param {string} p.rowsLabel        e.g. "BOY Benchmark (Sep 12, 2026)"
 * @param {string} p.columnsLabel     e.g. "Current Year"
 * @param {string} p.filtersLabel     one line describing active filters, or ''
 * @param {object} p.levelLabels      { doesNotMeet: 'Does not Meet Low', ... }
 * @param {object} p.counts           counts[prior][current] -> number
 * @param {object} p.names            names[prior][current] -> ['First L.', ...]
 * @param {object} [p.currentOnly]    { level: ['First L.'] }  tested now, not in baseline
 * @param {object} [p.baselineOnly]   { level: ['First L.'] }  in baseline, not tested now
 * @param {string} [p.footnote]
 * @param {Window} [existingWindow]  a window already opened in the click handler,
 *                                   so an async data load doesn't trip the popup blocker
 */
export const printGrowthMatrix = (p, existingWindow = null) => {
  const win = existingWindow || window.open('', '_blank');
  if (!win) return false;

  const header = `<thead><tr><th class="corner">${escapeHtml(p.rowsLabel)} &darr; / ${escapeHtml(
    p.columnsLabel
  )} &rarr;</th>${LEVEL_ORDER.map((l) => `<th>${escapeHtml(p.levelLabels[l] || l)}</th>`).join('')}</tr></thead>`;

  const body = `<tbody>${LEVEL_ORDER.map((prior) => {
    const cells = LEVEL_ORDER.map((current) => {
      const count = (p.counts[prior] && p.counts[prior][current]) || 0;
      const names = (p.names[prior] && p.names[prior][current]) || [];
      return `<td class="${movementClass(prior, current)}">${
        count ? `<div class="c">${count}</div>${namesList(names)}` : ''
      }</td>`;
    }).join('');
    return `<tr><th>${escapeHtml(p.levelLabels[prior] || prior)}</th>${cells}</tr>`;
  }).join('')}</tbody>`;

  const extraSection = (label, lists) => {
    if (!lists) return '';
    const blocks = LEVEL_ORDER.filter((l) => lists[l] && lists[l].length)
      .map(
        (l) =>
          `<div class="blk"><div class="bh">${escapeHtml(p.levelLabels[l] || l)} (${lists[l].length})</div>${namesList(
            lists[l]
          )}</div>`
      )
      .join('');
    if (!blocks) return '';
    return `<div class="extra"><div class="xh">${escapeHtml(label)}</div><div class="cols">${blocks}</div></div>`;
  };

  const now = new Date();
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8" /><title>${escapeHtml(p.title)}</title>
<style>
  @page { size: letter portrait; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { font-family: 'Source Sans Pro', 'Helvetica Neue', Arial, sans-serif; color: #222; margin: 0; padding: 16px; }
  .ph { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #222; padding-bottom: 6px; margin-bottom: 10px; }
  .ph .t { font-size: 17px; font-weight: 700; }
  .ph .m { font-size: 11px; color: #555; text-align: right; line-height: 1.35; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5px; table-layout: fixed; page-break-inside: auto; }
  th, td { border: 1px solid #999; padding: 4px 5px; vertical-align: top; }
  thead th { background: #eee; font-weight: 700; text-align: center; font-size: 9px; }
  thead th.corner { text-align: left; }
  tbody th { background: #eee; text-align: left; font-size: 9px; width: 72px; }
  tr { page-break-inside: avoid; }
  td .c { font-weight: 700; font-size: 9px; color: #444; margin-bottom: 2px; }
  ul { margin: 0; padding: 0; list-style: none; line-height: 1.3; }
  li { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  td.same { background: #fafafa; } td.up { background: #f3faf4; } td.down { background: #fdf3f3; }
  .pf { margin-top: 10px; font-size: 9px; color: #666; display: flex; justify-content: space-between; }
  .extra { margin-top: 16px; page-break-inside: avoid; }
  .xh { font-weight: 700; font-size: 11px; margin-bottom: 4px; border-bottom: 1px solid #999; }
  .cols { display: flex; flex-wrap: wrap; gap: 14px; font-size: 9.5px; }
  .blk { min-width: 140px; }
  .bh { font-weight: 700; margin-bottom: 2px; }
  .bar { margin: 0 0 12px; }
  .bar button { font: inherit; padding: 6px 14px; margin-right: 8px; }
  @media print { .bar { display: none; } body { padding: 0; } }
</style></head>
<body>
  <div class="bar"><button onclick="window.print()">Print / Save PDF</button><button onclick="window.close()">Close</button></div>
  <div class="ph">
    <div><div class="t">${escapeHtml(p.title)}</div></div>
    <div class="m">${escapeHtml(p.campusName || '')}<br/>Rows: ${escapeHtml(p.rowsLabel)} &rarr; Columns: ${escapeHtml(
    p.columnsLabel
  )}<br/>${escapeHtml(p.filtersLabel || 'All students')} &middot; printed ${escapeHtml(now.toLocaleDateString())}</div>
  </div>
  <table>${header}${body}</table>
  <div class="pf"><span>Tinted cells: green moved up, red moved down, grey held level.${
    p.footnote ? ` ${escapeHtml(p.footnote)}` : ''
  }</span><span>CoPilot</span></div>
  ${extraSection('Tested now, not in baseline', p.currentOnly)}
  ${extraSection('In baseline, not tested now', p.baselineOnly)}
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
};

export default printGrowthMatrix;
